import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Divider, Space, Table, Typography } from 'antd';
import { ArrowRight, CheckCircle, XCircle } from 'lucide-react';
import usePortfolioExchange from './usePortfolioExchange';
import { PortfolioTypes } from '../../utils/strings';
import Button from '@mui/material/Button';
import {
  addExchangePortfolioId,
  removeExchangePortfolioId
} from '../../store/slices/exchange.slice';
import ExchangeCart from './ExchangeCart';
import { Banner } from '../../components/Banner';
import {AssetUriMap} from "../../utils/assetUtils";
import IconChip from "../../components/Chips/IconChip";


const { Text } = Typography;
const ExchangeView = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { exchangeId } = useParams();
    const {
        exchanges,
        portfolios,
        loading,
        error,
        exchangeType
    } = usePortfolioExchange(exchangeId);

    // Get the selected portfolio IDs based on exchange type
    const selectedUserPortfolioIds = useSelector(state => state.exchange.selectedUserPortfolioIds);
    const selectedCompanyPortfolioIds = useSelector(state => state.exchange.selectedCompanyPortfolioIds);
    const companyPortfolios = useSelector(state => state.user.data?.company_details?.portfolios);
    const companyPortfolioIds = companyPortfolios?.map(p => p.id);
    const selectedPortfolioIds =  exchangeType === 'company' ? selectedCompanyPortfolioIds : selectedUserPortfolioIds;
    const totalPortfolios = portfolios.length;

    const portfolioAlreadyAdded = (portfolioId) => {
        if (exchangeType === 'company') {
            return companyPortfolioIds?.includes(portfolioId);
        }
        const portfolioObject = portfolios?.find(p => p.id === portfolioId);
        if (portfolioObject) {
            return portfolioObject._user?.hasOwnProperty('in_use');
        }
        return false;
    };

    const addedPortfolios = portfolios.filter(p => portfolioAlreadyAdded(p.id));


    const isPortfolioSelected = (portfolioId) => {
        return exchangeType === 'company'
            ? selectedCompanyPortfolioIds.includes(portfolioId)
            : selectedUserPortfolioIds.includes(portfolioId);
    };

    const handlePortfolioClick = (portfolioId) => {
        // Only allow selection if portfolio isn't already added
        if (!portfolioAlreadyAdded(portfolioId)) {
            if (isPortfolioSelected(portfolioId)) {
                dispatch(removeExchangePortfolioId(portfolioId));
            } else {
                dispatch(addExchangePortfolioId(portfolioId));
            }
        }
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    const exchange = exchanges?.find(e => e.fields.name === exchangeId);
    if (!exchange) return <div>Exchange not found</div>;

    const tableColumns = [
        {
            title: "Portfolio",
            key: 'name',
            dataIndex: 'name',
            render: (name) => <Text style={{ fontWeight: 500 }}>{name}</Text>,
        },
        {
            title: "Type",
            key: 'type',
            dataIndex: 'type',
            render: (type) => <Text>{PortfolioTypes[type]}</Text>
        },
        {
            title: "Targets",
            key: 'targets',
            dataIndex: 'targets',
            render: (targets) => <Text>{targets?.equity} / {targets?.fixed}</Text>
        },
        {
            title: "Targets",
            key: 'category',
            dataIndex: ['tags', 'category'],
            render: (text) => <Text>{text}</Text>
        },
        {
          title: "View",
            key: 'view',
            render: (_, record) => (
                <Button
                    variant="text"
                    color="info"
                    endIcon={<ArrowRight size={16}/>}
                    onClick={() => navigate(`/public/portfolio/${record.id}`)}
                >
                    View
                </Button>
            ),
            align: 'center'
        },
        {
            title: "Status",
            key: '_isAddedToUser',
            dataIndex: '_isAddedToUser',
            render: (_, record) => {
                const isAdded = portfolioAlreadyAdded(record.id);
                const isSelected = isPortfolioSelected(record.id);

                if (isAdded) {
                    return <XCircle color="red" size={20} />;
                }

                return <CheckCircle
                    color={isSelected ? "green" : "gray"}
                    size={20}
                />;
            },
        },
    ];

    return (
        <div>
            <Banner title={exchange.fields.name}
                    imgUrl={exchange.fields.imgUrl}
                    description={exchange.fields.description}
                    color={'#fff'}
                    topLeftText={'Back to Exchanges'}
                    topLeftAction={() => navigate('/portfolios/exchange')}
            />
            <div style={{padding: '20px', borderRadius: '8px'}}>
                {/* Exchange Header Section */}
                {/* Portfolios Section */}
                <div className="mt-4 w-full xl:w-4/5 mx-auto">
                    <Space>
                        <IconChip icon={AssetUriMap.total} label={`${addedPortfolios.length} Portfolios Added`} bordered={false} />
                        <IconChip icon={AssetUriMap.selectedItems} label={`${selectedPortfolioIds.length} Selected`} bordered={false} />
                        <IconChip icon={AssetUriMap.itemsAvailable} label={`${totalPortfolios - addedPortfolios.length - selectedPortfolioIds.length} Available`} bordered={false} />
                    </Space>
                    <ExchangeCart/>
                    <Divider/>
                    <Table
                        dataSource={portfolios}
                        columns={tableColumns}
                        rowKey="id"
                        pagination={false}
                        className="mt-4"
                        style={{borderRadius: '8px', overflow: 'hidden'}}
                        onRow={(record) => ({
                            onClick: () => handlePortfolioClick(record.id),
                            style: {
                                cursor: portfolioAlreadyAdded(record.id) ? 'not-allowed' : 'pointer'
                            }
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default ExchangeView;